import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { renderQrCard } from '../lib/qrCard';

/**
 * "Share guest link" modal — twin of QrCodeModal, but built around the
 * external-guest password gate at `/gast`. The QR encodes the gate URL
 * only (never the password — same security stance as the slot-QR pattern
 * documented in the 2026-05-16 journal entry: "stolen QR can't
 * authenticate on its own"). The shared password is displayed in plain
 * text inside the modal so the warden can read it off when handing the
 * link to a guest.
 *
 * Renders a 512px QR onto a canvas client-side via the `qrcode` package
 * already in the deps; no Worker round-trip. Reuses the same backdrop /
 * ESC / portal-to-body pattern as QrCodeModal.
 */
type Props = {
  /** The plaintext password to display below the QR for the admin to read
   * off. Always shown — the modal is admin-only chrome. */
  password: string;
  onClose(): void;
};

export function GuestLinkModal({ password, onClose }: Props) {
  const { t } = useTranslation();
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState<'link' | 'password' | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [shareErr, setShareErr] = useState<string | null>(null);

  const url = useMemo(() => `${window.location.origin}/gast`, []);
  // Footer URL = host + the gate path, no protocol — printed under the
  // QR as a human-readable hint so a reader who can't scan can still
  // type the destination by hand.
  const footerUrl = useMemo(() => window.location.host + '/gast', []);

  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  // ESC closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Composite QR card (title + QR-with-centre-logo + footer URL), same
  // renderer as the slot QR modal so the two stay visually consistent.
  useEffect(() => {
    let alive = true;
    renderQrCard(url, footerUrl)
      .then((d) => {
        if (alive) setDataUrl(d);
      })
      .catch(() => {
        if (alive) setDataUrl(null);
      });
    return () => {
      alive = false;
    };
  }, [url, footerUrl]);

  const flashCopied = (kind: 'link' | 'password') => {
    setCopied(kind);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(null), 2000);
  };

  const onDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'qr-gast-link.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      flashCopied('link');
    } catch {
      setShareErr(t('admin.calendar.shareGuestLinkCopyFailed'));
    }
  };

  const onCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
      flashCopied('password');
    } catch {
      setShareErr(t('admin.calendar.shareGuestLinkCopyFailed'));
    }
  };

  // Mirrors the share probe in QrCodeModal — only render the Share button
  // when the runtime actually supports sharing PNG files (mobile Safari /
  // Chrome, modern Android, some desktop Edge). Desktop without the API
  // falls back to Download.
  const canShareFiles = useMemo(() => {
    if (typeof navigator === 'undefined' || !navigator.canShare) return false;
    try {
      const probe = new File([new Blob([''], { type: 'image/png' })], 'probe.png', {
        type: 'image/png',
      });
      return navigator.canShare({ files: [probe] });
    } catch {
      return false;
    }
  }, []);

  const onShare = async () => {
    if (!dataUrl) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'qr-gast-link.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        // text carries BOTH URL and password — do not also pass `url:` here.
        // WhatsApp (and some others) concatenate the `url:` value onto the
        // text body verbatim, which would render the URL twice and smash
        // it against the password line, making the password hard to read
        // and impossible to select on its own. With text-only the URL is
        // linkified once and the password sits in its own paragraph.
        await navigator.share({
          files: [file],
          title: t('admin.calendar.shareGuestLink'),
          text: t('admin.calendar.shareGuestLinkShareText', { url, password }),
        });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (!/abort|cancel/i.test(msg)) setShareErr(msg);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="guest-link-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 dark:bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-sm w-full"
      >
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-3">
          <h3
            id="guest-link-title"
            className="text-sm font-semibold text-slate-800 dark:text-slate-100"
          >
            {t('admin.calendar.shareGuestLink')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-md p-1.5 text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-600 dark:text-slate-400">
            {t('admin.calendar.shareGuestLinkHelp')}
          </p>

          <div className="w-full bg-white border border-slate-200 rounded-md flex items-center justify-center min-h-[200px]">
            {dataUrl ? (
              <img
                src={dataUrl}
                alt={t('admin.calendar.shareGuestLink')}
                className="w-full h-auto object-contain"
              />
            ) : (
              <span className="text-xs text-slate-400">…</span>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              {t('admin.calendar.shareGuestLinkUrl')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={url}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-xs tabular-nums"
              />
              <button
                type="button"
                onClick={() => void onCopyLink()}
                className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-xs font-medium"
              >
                {copied === 'link'
                  ? t('admin.calendar.shareGuestLinkCopied')
                  : t('admin.calendar.shareGuestLinkCopy')}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              {t('admin.calendar.shareGuestLinkPassword')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={password}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-xs font-mono"
              />
              <button
                type="button"
                onClick={() => void onCopyPassword()}
                className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-xs font-medium"
              >
                {copied === 'password'
                  ? t('admin.calendar.shareGuestLinkCopied')
                  : t('admin.calendar.shareGuestLinkCopy')}
              </button>
            </div>
          </div>

          {shareErr && (
            <p
              role="alert"
              className="text-xs text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-2 py-1"
            >
              {shareErr}
            </p>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            {canShareFiles && (
              <button
                type="button"
                onClick={() => void onShare()}
                disabled={!dataUrl}
                className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              >
                {t('admin.calendar.shareGuestLinkShareButton')}
              </button>
            )}
            <button
              type="button"
              onClick={onDownload}
              disabled={!dataUrl}
              className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
            >
              {t('admin.calendar.shareGuestLinkDownload')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** The shared password seeded in migration 0009. Bumped here when rotated.
 * Kept inline (not loaded from /api/me) because the modal is admin-only
 * chrome and there's no rotation UI yet — the warden reads this off the
 * modal exactly as printed beside any QR sticker. */
export const EXTERNAL_GUEST_PASSWORD = 'Rossberg1385';
